import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Arrow, ComingSoon, EASE, Eyebrow, MaskLine } from "../site/motion";

const TiltImage = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
          className="spotlight-frame relative aspect-[4/5] overflow-hidden bg-dano-ink"
        >
          <motion.img initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: EASE, delay: 0.35 }} src="/images/chocolate.jpg" alt="Chocolate Dano Bar" className="h-full w-full object-cover" data-testid="hero-product-image" />
        </motion.div>
        <div className="absolute -left-4 sm:-left-10 bottom-10 [transform:translateZ(60px)]">
          <div className="rounded-2xl border-2 border-dano-ink bg-dano-cream px-4 py-3 shadow-[6px_6px_0_#1A1714]">
            <p className="font-cond text-xs uppercase tracking-[0.2em] text-dano-red">No. 02 · 100g · ~25g protein</p>
            <p className="font-wide text-lg uppercase leading-tight">Chocolate</p>
          </div>
        </div>
        <div className="absolute -right-2 top-8 [transform:translateZ(80px)]"><ComingSoon testId="hero-coming-soon-badge" /></div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24">
      <div className="pointer-events-none absolute -right-40 top-10 h-[620px] w-[620px] rounded-full bg-dano-gold/25 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Eyebrow>Real-food protein bars · danobars.com</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-wide uppercase leading-[0.88] text-[12.6vw] sm:text-[11vw] lg:text-[7.6vw] 2xl:text-[112px]" data-testid="hero-heading">
            <MaskLine i={0}>Real food.</MaskLine>
            <MaskLine i={1}>Real <span className="text-dano-red">protein.</span></MaskLine>
            <MaskLine i={2} className="text-outline">Rain or shine.</MaskLine>
          </h1>
          <Arrow className="mt-2 h-8 sm:h-12 w-[88%] text-dano-red" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }} className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-dano-ink/80">
            Premium, handcrafted protein bars made with real, recognizable ingredients. Bigger, softer, chewier: 100g with approximately 25g of protein.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease: EASE }} className="mt-9 flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-cta-shop-now" className="btn-press group inline-flex items-center gap-3 rounded-full bg-dano-ink px-7 py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-red">
              See the bars <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/our-story" data-testid="hero-cta-our-story" className="btn-press inline-flex items-center rounded-full border-2 border-dano-ink px-7 py-4 font-cond text-lg uppercase tracking-widest hover:bg-dano-ink hover:text-dano-cream">
              Our story
            </Link>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="mx-auto w-full max-w-[480px] lg:col-span-5 lg:max-w-none">
          <TiltImage />
        </motion.div>
      </div>
    </section>
  );
};
